import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShareCard = ({ share, onRevoke, onViewDetails, type = 'internal' }) => {
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success bg-success/10';
      case 'expired': return 'text-error bg-error/10';
      case 'pending': return 'text-warning bg-warning/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getExpirationText = (expiresAt, status) => {
    if (status === 'expired') return 'Expired';
    if (!expiresAt) return 'No expiration';
    
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffHours = Math.ceil((expiry - now) / (1000 * 60 * 60));
    
    if (diffHours < 24) return `${diffHours}h remaining`;
    const diffDays = Math.ceil(diffHours / 24);
    return `${diffDays}d remaining`;
  };

  const handleRevoke = () => {
    onRevoke(share?.id);
    setShowRevokeDialog(false);
  };

  return (
    <>
      <div className="bg-card border border-border rounded-lg p-6 hover:shadow-card transition-smooth">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              type === 'internal' ? 'bg-primary/10' : 'bg-accent/10'
            }`}>
              <Icon 
                name={type === 'internal' ? 'Users' : 'ExternalLink'} 
                size={20} 
                className={type === 'internal' ? 'text-primary' : 'text-accent'}
              />
            </div>
            <div>
              <h3 className="font-medium text-foreground">{share?.recipientName}</h3>
              <p className="text-sm text-muted-foreground">{share?.recipientEmail}</p>
              {type === 'internal' && share?.department && (
                <p className="text-xs text-muted-foreground">{share?.department}</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(share?.status)}`}>
              {share?.status}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onViewDetails(share)}
              className="h-8 w-8"
            >
              <Icon name="MoreVertical" size={16} />
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Shared Items:</span>
            <span className="font-medium text-foreground">{share?.itemCount} items</span>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Access Level:</span>
            <span className="font-medium text-foreground capitalize">{share?.accessLevel}</span>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Expires:</span>
            <span className="font-medium text-foreground">
              {getExpirationText(share?.expiresAt, share?.status)}
            </span>
          </div>

          {share?.usageLimit && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Usage:</span>
              <span className="font-medium text-foreground">
                {share?.usageCount}/{share?.usageLimit} times
              </span>
            </div>
          )}

          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Last Access:</span>
            <span className="font-medium text-foreground">
              {share?.lastAccess ? new Date(share.lastAccess)?.toLocaleDateString() : 'Never'}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <Icon name="Calendar" size={14} />
            <span>Created {new Date(share.createdAt)?.toLocaleDateString()}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewDetails(share)}
            >
              View Details
            </Button>
            {share?.status === 'active' && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowRevokeDialog(true)}
              >
                Revoke
              </Button>
            )}
          </div>
        </div>
      </div>
      {/* Revoke Confirmation Dialog */}
      {showRevokeDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-300">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-error/10 rounded-full flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-error" />
              </div>
              <div>
                <h3 className="font-medium text-foreground">Revoke Share Access</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-sm text-foreground mb-6">
              Are you sure you want to revoke access for <strong>{share?.recipientName}</strong>? 
              They will immediately lose access to all shared items.
            </p>
            
            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowRevokeDialog(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleRevoke}
              >
                Revoke Access
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareCard;